import { useEffect, useRef, useState } from "react";
import Card from "../../../elements/Card";
import Select from "../../../form/select/Select";
import { useParams } from "react-router-dom";
import useClickOutside from "../../../../hooks/useClickOutside";
import { useQuery } from "@tanstack/react-query";
import { categoryList } from "../../../../services/categoryService";
import { lngDetect } from "../../../../helpers/lngDetect";
import { organizationList } from "../../../../services/organizationService";
import { toast } from "react-toastify";
import Tags from "../../../form/Tags";
import { classNames } from "../../../../helpers/classNames";
import Badge from "../../../elements/Badge";
import { t } from "i18next";

const ReadOnly = ({ data, eventType, eventCategory, organization }) => {
  // Helper function to find item by ID
  const findItemById = (items, id) => {
    return items?.find((item) => item?._id === id);
  };

  const selectedType = findItemById(eventType?.data, data?.type);
  const selectedCategory = findItemById(eventCategory?.data, data?.category);
  const selectedOrganization = findItemById(
    organization?.data,
    data?.organization
  );

  return (
    <Card
      bodyClass={"space-y-6"}
      className={" py-4 md:py-8 px-3 md:px-6 mt-3 md:mt-auto"}
    >
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-6 space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{t("Event Type")}</h3>
            <div className="text-text-gradient-red font-medium">
              {selectedType ? lngDetect(selectedType?.name) : t("Not selected")}
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{t("Organiser")}</h3>
            <div className="text-text-gradient-red font-medium">
              {selectedOrganization
                ? lngDetect(selectedOrganization?.name)
                : t("Not selected")}
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{t("Event Category")}</h3>
            <div className="text-text-gradient-red font-medium">
              {selectedCategory
                ? lngDetect(selectedCategory?.name)
                : t("Not selected")}
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{t("Tags")}</h3>
            <div className="text-text-gradient-red font-medium">
              {data?.tags && data.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1 space-y-1">
                  {data.tags.map((tag, index) => (
                    <Badge
                      key={index}
                      className={"text-purple-600 bg-purple-100"}
                      label={
                        typeof tag === "string"
                          ? tag
                          : tag?.label || tag?.value || tag?.name || ""
                      }
                    />
                  ))}
                </div>
              ) : (
                t("No tags added")
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const EventCatType = ({
  values = {},
  handleChange = () => {},
  formValidate = () => {},
  errors = {},
}) => {
  const divRef = useRef(null);
  const { eventId } = useParams();
  const [readOnly, setReadOnly] = useState(false);

  useClickOutside(divRef, () => {
    if (eventId) {
      setReadOnly(true);
    } else {
      setReadOnly(false);
    }
  });

  // Check if there are any errors in this component
  const hasErrors = () => {
    const errorFields = ["type", "category", "organization"];
    return errorFields.some((field) => errors[field]);
  };

  // Auto-open edit mode when errors are present
  useEffect(() => {
    if (hasErrors() && eventId) {
      setReadOnly(false);
    }
  }, [errors, eventId]);

  const { data: eventType } = useQuery({
    queryKey: ["event-type"],
    queryFn: async () => {
      const res = await categoryList({ type: "event-type", limit: 30 });
      return res?.docs;
    },
  });

  const { data: eventCategory } = useQuery({
    queryKey: ["event-category"],
    queryFn: async () => {
      const res = await categoryList({ type: "event-category", limit: 30 });
      return res?.docs;
    },
  });

  const {
    data: organization,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["organization"],
    queryFn: async () => {
      const res = await organizationList({ limit: 30 });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  useEffect(() => {
    if (eventId) {
      setReadOnly(true);
    }
  }, [eventId]);

  return (
    <div
      ref={divRef}
      onClick={() => {
        if (eventId) {
          setReadOnly(false);
        }
      }}
      className={hasErrors() ? "border-2 border-red-300 rounded-md p-2" : ""}
    >
      {readOnly && !hasErrors() ? (
        <ReadOnly
          data={values}
          eventType={eventType}
          eventCategory={eventCategory}
          organization={organization}
        />
      ) : (
        <Card bodyClass={"space-y-6"}>
          {hasErrors() && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
              <div className="flex items-center">
                <i className="fa-regular fa-exclamation-triangle text-red-500 mr-2"></i>
                <span className="text-red-700 font-medium">
                  {t("Please complete the required fields below")}
                </span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-12 gap-5 mt-4 mb-4">
            <div className="col-span-6">
              <div className="relative">
                <Select
                  dropdownData={eventType?.data?.map((item) => ({
                    name: lngDetect(item?.name),
                    value: item?._id,
                  }))}
                  placeholder={t("Select event type")}
                  selectedValue={values?.type}
                  onChange={(val) =>
                    handleChange({ target: { name: "type", value: val } })
                  }
                  label="Type"
                  xPlacement="bottomLeft"
                  dropdownButtonClass={classNames(
                    "border rounded-md",
                    errors.type ? "border-red-300" : "border-gray-300"
                  )}
                  dropdownClass="w-full rounded-md"
                  labelClasses={classNames("text-lg")}
                  {...formValidate(errors, "type")}
                />
              </div>
            </div>
            <div className="col-span-6">
              <div className="relative">
                <Select
                  dropdownData={eventCategory?.data?.map((item) => ({
                    name: lngDetect(item?.name),
                    value: item?._id,
                  }))}
                  label={t("Category")}
                  xPlacement="bottomLeft"
                  dropdownButtonClass={classNames(
                    "border rounded-md",
                    errors.category ? "border-red-300" : "border-gray-300"
                  )}
                  dropdownClass="w-full rounded-md"
                  labelClasses={classNames("text-lg")}
                  placeholder={t("Select event category")}
                  selectedValue={values?.category}
                  onChange={(val) =>
                    handleChange({ target: { name: "category", value: val } })
                  }
                  {...formValidate(errors, "category")}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-5 mt-4">
            <div className="col-span-6">
              <div
                className={classNames(
                  "text-lg font-bold",
                  errors.organization ? "text-red-600" : "text-black"
                )}
              >
                {t("Organised by")}
                {errors.organization && (
                  <span className="text-red-500 text-sm ml-2">*Required</span>
                )}
              </div>
              <div className="text-sm text-slate-500 font-medium">
                {t("Your event will appear on this organiser's profile page.")}
              </div>
              <div className="relative mb-1 mt-3">
                <Select
                  dropdownData={organization?.data?.map((item) => ({
                    name: lngDetect(item?.name),
                    value: item?._id,
                  }))}
                  label={t("Organiser")}
                  xPlacement="bottomLeft"
                  dropdownButtonClass={classNames(
                    "border rounded-md",
                    errors.organization ? "border-red-300" : "border-gray-300"
                  )}
                  dropdownClass="w-full rounded-md"
                  labelClasses={classNames(
                    "text-lg",
                    errors.organization ? "text-red-600" : ""
                  )}
                  placeholder={t("Select event organizer")}
                  selectedValue={values?.organization}
                  onChange={(val) =>
                    handleChange({
                      target: { name: "organization", value: val },
                    })
                  }
                  {...formValidate(errors, "organization")}
                />
              </div>
            </div>
            <div className="col-span-6">
              <div className="text-lg text-black font-bold">{t("Tags")}</div>
              <div className="text-sm text-slate-500 font-medium">
                {t(
                  "Help people discover your event by adding tags related to your event's theme, topic, vibe, location, and more."
                )}
              </div>
              <div className="relative mb-1 mt-3">
                <Tags
                  required
                  value={values?.tags}
                  dropdownData={values?.tags}
                  onChange={(val) => {
                    handleChange({ target: { name: "tags", value: val } });
                  }}
                  placeholder={t("Add search keywords to your event")}
                  labelClasses="text-gray-700"
                />
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default EventCatType;
